import { C_Regexp } from '@/ts/constants/C_Regexp';

import { getPresaleEndMs } from './getPresaleEndMs';

import type { T_Pool } from '@/ts/types/T_Pool';

export function normalizeTokenSymbol(
  tokenSymbol: string | null | undefined
): string {
  if (!tokenSymbol) return '';
  return tokenSymbol.replace(C_Regexp.txPrefix, '').toLowerCase().trim();
}

export function getPoolTokenSymbols(
  pools: T_Pool[] | null | undefined,
  options?: { normalize?: boolean }
): string[] {
  if (!pools || pools.length === 0) {
    return [];
  }

  const shouldNormalize = options?.normalize !== false; // Default to true for backward compatibility

  return pools
    .map((pool: T_Pool) => {
      if (shouldNormalize) {
        return normalizeTokenSymbol(pool.tokenSymbol);
      }
      return pool.tokenSymbol || '';
    })
    .filter((sym: string) => Boolean(sym));
}

export function getFirstPoolSlug(
  pools: T_Pool[] | null | undefined
): string | null {
  const slugs = getPoolSlugs(pools);
  return slugs[0] ?? null;
}

export function getFirstPoolTokenSymbol(
  pools: T_Pool[] | null | undefined
): string | null {
  const tokenSymbols = getPoolTokenSymbols(pools);
  return tokenSymbols[0] ?? null;
}

export function findPoolByUUID(
  pools: T_Pool[] | null | undefined,
  uuid: string | null | undefined
): T_Pool | undefined {
  if (!pools || !uuid) return undefined;
  return pools.find(pool => pool.uuid === uuid);
}

export function findPoolBySymbol(
  pools: T_Pool[] | null | undefined,
  requestedSymbol: string | null | undefined
): T_Pool | undefined {
  if (!pools || !requestedSymbol) return undefined;
  const normalizedSymbol = requestedSymbol.toLowerCase().trim();
  return pools.find(pool => {
    const poolSymbol = normalizeTokenSymbol(pool.tokenSymbol);
    return poolSymbol === normalizedSymbol;
  });
}

export function findPoolBySlug(
  pools: T_Pool[] | null | undefined,
  slug: string | null | undefined
): T_Pool | undefined {
  if (!pools || !slug) return undefined;
  const normalizedSlug = slug.toLowerCase().trim();
  return pools.find(pool => pool.slug?.toLowerCase() === normalizedSlug);
}

export function getPoolSlugs(pools: T_Pool[] | null | undefined): string[] {
  if (!pools || pools.length === 0) {
    return [];
  }

  return pools
    .map((pool: T_Pool) => pool.slug)
    .filter((slug: string) => Boolean(slug));
}

export function getTokenSymbolFromPool(
  pool: T_Pool | null | undefined,
  fallback: string | null | undefined = null
): string {
  if (!pool?.tokenSymbol) {
    return fallback?.toLowerCase() || '';
  }
  return normalizeTokenSymbol(pool.tokenSymbol);
}

export function getPoolsLinkHref(
  pools: T_Pool[] | null | undefined,
  options?: { fallbackPresaleHref?: string }
): string {
  const fallbackPresaleHref = options?.fallbackPresaleHref || '/pools-presale';

  if (!pools || pools.length === 0) {
    return fallbackPresaleHref;
  }

  const hasActivePresale = pools.some(pool => {
    const presaleEndMs = getPresaleEndMs(pool.presaleEndDate);
    return presaleEndMs !== null && Date.now() < presaleEndMs;
  });

  return getPoolsNavHref({
    hasActivePresale,
    firstPoolSlug: pools[0]?.slug,
    fallbackPresaleHref
  });
}

export function getPoolsNavHref({
  hasActivePresale,
  firstPoolSlug,
  fallbackPresaleHref = '/pools-presale'
}: {
  hasActivePresale: boolean;
  firstPoolSlug?: string | null;
  fallbackPresaleHref?: string;
}): string {
  if (hasActivePresale) {
    return fallbackPresaleHref;
  }

  if (firstPoolSlug) {
    return `/pools/${firstPoolSlug}`;
  }

  return fallbackPresaleHref;
}
